import {Location} from "@angular-material-extensions/google-maps-autocomplete";
import {Injectable} from "@angular/core";
import {GenericTypeDef} from "../../../../../../soko-console-web-v2/src/app/core/model/generic-type-def.model";
import GeocoderAddressComponent = google.maps.GeocoderAddressComponent;
import PlaceResult = google.maps.places.PlaceResult;

export class Address {
  [key: string]: any;
  id: number = 0;
  retailerId: number = 0;
  addressType: GenericTypeDef = new GenericTypeDef();
  administrativeAreaLevel1: string = '';
  administrativeAreaLevel1Code: string = '';
  administrativeAreaLevel2: string = '';
  country: string = '';
  countryCode: string = '';
  formattedAddress: string | undefined = '';
  latitude: number = 0;
  locality: string = '';
  longitude: number = 0;
  mapsUrl: string | undefined = '';
  placeId: string | undefined = '';
  placeName: string = '';
  postal_code: string = '';
  route: string = '';
  streetNumber: number = 0;
  sublocalityLevel1: string = '';
}

export class AddressObject {
  location!: Location;
  place!: PlaceResult;
}

export class RetailerAddress {
  address: any;
  addressPlace: any;
}

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  googleMapsLoaded = false;

  constructor() {
  }

  loadGoogleMapsAPI(env: any): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined') {
        this.googleMapsLoaded = true;
        console.log('Google Maps API already loaded.');
        resolve();
      } else {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${env.googleMapsApiKey}&libraries=places`;

        script.onload = () => {
          this.googleMapsLoaded = true;
          console.log('Google Maps API loaded successfully.');
          resolve();
        };

        script.onerror = (err) => {
          console.error('Error loading Google Maps API:', err);
          reject(err);
        };

        document.body.appendChild(script);
      }
    });
  }

  public parseAddressData(location: Location, place: PlaceResult, addressTypeCode?: string): RetailerAddress {
    const address = this.decodeAddressComponents(place.address_components);
    address.addressType.code = addressTypeCode ? addressTypeCode : 'PHYSICAL';
    address.formattedAddress = place.formatted_address;
    address.latitude = location.latitude;
    address.longitude = location.longitude;
    address.mapsUrl = place.url;
    address.placeId = place.place_id;
    address.placeName = place.name;

    const addressPlace = {
      placeId: place.place_id,
      place: JSON.stringify(place)
    };

    return {address, addressPlace};
  }

  public parseJsonAddressData(location: Location, place: PlaceResult, addressTypeCode?: string): RetailerAddress {
    const address = this.decodeAddressComponents(place.address_components);
    address.addressTypeCode = addressTypeCode ? addressTypeCode : 'PHYSICAL';
    address.formatted_address = place.formatted_address;
    address.lat = location.latitude;
    address.long = location.longitude;
    address.maps_url = place.url;
    address.place_id = place.place_id;
    address.place_name = place.name;

    const addressPlace = {
      place_id: place.place_id,
      place: JSON.stringify(place)
    };

    return {address, addressPlace};
  }

  public geocodeAddressFromFormattedString(formattedAddress: string): Promise<AddressObject | null> {
    if (this.googleMapsLoaded && google) {
      const geocoder = new google.maps.Geocoder();

      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: formattedAddress }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
            const { lat, lng } = results[0].geometry.location.toJSON();
            const location: Location = { latitude: lat, longitude: lng };
            const placeResult: google.maps.places.PlaceResult = this.mapGeocoderResultToPlaceResult(results[0]);
            resolve({ location, place: placeResult });
            return { location, place: placeResult };
          } else {
            reject(`Geocoding failed: ${status}`)
            return null;
          }
        });
      });
    }
    return new Promise(() => null);
  }

  private decodeAddressComponents(addressComponents?: GeocoderAddressComponent[]): any {
    const address: Address = new Address();
    // @ts-ignore
    addressComponents.forEach((addr: GeocoderAddressComponent) => {
      const type = this.formatAddressTypeKeys(addr.types[0]);
      address[type] = addr.long_name;

      if (type.includes('country') || type.includes('administrative_area_level_1')) {
        address[type + 'Code'] = addr.short_name;
      }
    });
    return address;
  }

  private formatAddressTypeKeys(key: string): string {
    const splitString = key.split('_');
    const capitalized = splitString.map((m, i) => {
      if (i === 0) { return m; }
      return m.charAt(0).toUpperCase() + m.slice(1);
    });
    const formatted = capitalized.join('');
    return formatted;
  }

  private mapGeocoderResultToPlaceResult(geocoderResult: google.maps.GeocoderResult): google.maps.places.PlaceResult {
    return {
      formatted_address: geocoderResult.formatted_address,
      geometry: geocoderResult.geometry,
      address_components: geocoderResult.address_components,
      // Populate additional fields as needed (set undefined for unavailable fields)
      icon: undefined,
      name: '',
      photos: undefined,
      place_id: geocoderResult.place_id,
      plus_code: undefined,
      types: geocoderResult.types,
      url: undefined,
      utc_offset_minutes: undefined,
      vicinity: undefined,
    };
  }
}
